// react
import { useState, Dispatch, SetStateAction, useEffect } from "react";
import { useTranslation } from "react-i18next";
// node-modules
import axios from "axios";
// mui
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  useTheme,
  styled,
} from "@mui/material";
// src
import { config } from "../config/config";
import { MsgSnackbar } from "./MsgSnackbar";
import { IEnabledUpdateProjectDetail } from "./ProjectDetail/Interfaces/ProjectDetailInterfaces";
import { pjNameValidationCheck } from "./Utility/ValidationUtil";

type Props = {
  projectID: string;
  projectName: string;
  setProjectName?: Dispatch<SetStateAction<string>>;
  updateProjectDetail?: (newDetail: IEnabledUpdateProjectDetail) => void;
  openDialog: boolean;
  setOpenDialog?: Dispatch<SetStateAction<boolean>>;
};

const RenameProjectDialog = (props: Props) => {
  const { t } = useTranslation();
  // const [openDialog, setOpenDialog] = useState(false);
  const [renameProject, setRenameProject] = useState("");
  const [existingProjectName, setExistingProjectName] = useState(
    props.projectName
  );
  const [isValid, setIsValid] = useState(true);

  // Snackbar
  const [snackbar, setSnackbar] = useState(false);
  const [msg, setMsg] = useState("");

  const handleSnackbarClose = async () => {
    setSnackbar(false);
  };
  const rename = async () => {
    try {
      const renamePj = { name: renameProject };
      await axios
        .put(
          `${config.serverHost}/api/v1/project/${props.projectID}`,
          renamePj,
          {
            withCredentials: true,
          }
        )
        .then(async () => {
          setSnackbar(true);
          setMsg(t("OK"));
          setExistingProjectName(renamePj.name);
          props.setProjectName && props.setProjectName(renamePj.name);
          props.updateProjectDetail &&
            props.updateProjectDetail({ name: renamePj.name });
        });
      props.setOpenDialog(false);
    } catch (e) {
      console.error(`Error: ${e}`);
    }
  };

  const handleClickOpen = () => {
    setIsValid(true);
    props.setOpenDialog(true);
  };

  const handleClose = () => {
    props.setOpenDialog(false);
  };

  const onChangeHandle = (e: string) => {
    setRenameProject(e);

    setIsValid(pjNameValidationCheck(e));
  };

  const theme = useTheme();

  const customColor = {
    color: theme.palette.base.contrast,
  };

  const customModalColor = {
    color: theme.palette.base.modal,
  };

  const customDialogBackgroundColor = {
    color: theme.palette.base.dialogBackground,
  };

  const CustomCanselButton = styled(Button)({
    background: theme.palette.base.modal,
    color: theme.palette.base.contrast,
    boxShadow: "none",
    "&:hover": {
      background: theme.palette.base.modal,
      color: theme.palette.base.contrast,
      boxShadow: "none",
    },
  });

  const customLoadingButtonColor = {
    background: theme.palette.primary.main,
    boxShadow: "none",
    color: "#FFFFFF",
  };

  useEffect(() => {
    setRenameProject(props.projectName);
  }, [props.projectName, props.openDialog]);

  return (
    <>
      <Dialog
        open={props.openDialog}
        PaperProps={{
          style: {
            width: "583px",
            height: "214px",
            background: customModalColor.color,
            boxShadow: "none",
            textAlign: "center",
            border: "1px solid #44444F",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: customDialogBackgroundColor.color,
            },
          },
        }}
      >
        <DialogTitle
          style={{
            color: customColor.color,
            textAlign: "left",
            fontSize: "18px",
          }}
        >
          {t("CHANGE_TITLE")}
          <Button
            style={customColor}
            className="x-btn"
            onClick={handleClose}
            sx={{
              float: "right",
              fontSize: "18px",
              margin: "0px",
              padding: "0px",
              position: "absolute",
              right: "0px",
            }}
          >
            ×
          </Button>
          <div className="border-comm" />
        </DialogTitle>
        <DialogContent>
          {isValid ? (
            <TextField
              autoFocus
              margin="dense"
              hiddenLabel
              id="name outlined-basic"
              placeholder={t("PROJECT")}
              onChange={(e) => onChangeHandle(e.target.value)}
              defaultValue={existingProjectName}
              type="string"
              variant="filled"
              style={{
                height: "42px",
                width: "532px",
                color: "#44444F",
                textAlign: "left",
                fontSize: "18px",
              }}
              InputProps={{
                style: {
                  height: "42px",
                  backgroundColor: customModalColor.color,
                  border: "1px solid #44444F",
                },
              }}
            />
          ) : (
            <TextField
              error
              helperText={t("INVALID_CHARACTERS")}
              autoFocus
              margin="dense"
              hiddenLabel
              id="name outlined-basic"
              placeholder={t("PROJECT")}
              onChange={(e) => onChangeHandle(e.target.value)}
              defaultValue={existingProjectName}
              type="string"
              variant="filled"
              style={{
                height: "42px",
                width: "532px",
                color: "#44444F",
                textAlign: "left",
                fontSize: "18px",
              }}
              InputProps={{
                style: {
                  height: "42px",
                  backgroundColor: customModalColor.color,
                  border: "1px solid #44444F",
                },
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <CustomCanselButton
            className="re-btn-com re-btn1"
            onClick={handleClose}
          >
            {t("CANCEL")}
          </CustomCanselButton>
          <Button
            style={
              isValid && renameProject.trim() !== ""
                ? { ...customLoadingButtonColor, boxShadow: "none" }
                : {
                    background:
                      theme.palette.mode === "dark" ? "#2a2a2d" : "#e0e0e0",
                    boxShadow: "none",
                  }
            }
            className="re-btn-com re-btn2"
            onClick={rename}
            disabled={!isValid || renameProject.trim() === ""}
          >
            {t("SAVE")}
          </Button>
        </DialogActions>
      </Dialog>
      <MsgSnackbar open={snackbar} onClose={handleSnackbarClose} msg={msg} />
    </>
  );
};

export default RenameProjectDialog;
